import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { encryptData } from '../../utils/cryptoUtils';
import '../common.scss';

// ----------------------------------------------------------------------
// Row group definitions (key -> label). Used to render editable tables
// without repeating JSX for every single line item.
// ----------------------------------------------------------------------
const PL_INCOME_ROWS = [
  { key: 'revenue_from_operations', label: 'Revenue from Operations' },
  { key: 'other_income', label: 'Other Income' },
];

const PL_COGS_ROWS = [
  { key: 'opening_stock', label: 'Opening Stock' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'closing_stock', label: 'Less: Closing Stock' },
  { key: 'direct_expenses', label: 'Direct Expenses' },
];

const PL_EXPENSE_ROWS = [
  { key: 'employee_benefit_expenses', label: 'Employee Benefit Expenses' },
  { key: 'selling_expenses', label: 'Selling Expenses' },
  { key: 'bank_charges', label: 'Bank Charges' },
  { key: 'interest_paid', label: 'Interest Paid' },
  { key: 'depreciation_amortisation_expenses', label: 'Depreciation and Amortisation Expenses' },
  { key: 'partners_director_salary', label: "Partners/Director's Salary" },
  { key: 'other_expenses', label: 'Other Expenses' },
];

const PL_BELOW_ROWS = [
  { key: 'exceptional_item', label: 'Exceptional Item' },
  { key: 'tax', label: 'Tax' },
];

const BS_EQUITY_ROWS = [
  { key: 'share_capital', label: 'Share Capital' },
  { key: 'reserves_and_surplus', label: 'Reserves and Surplus' },
  { key: 'quasi_equity', label: 'Quasi Equity' },
];

const BS_NONCURRENT_LIAB_ROWS = [
  { key: 'secured_borrowings', label: 'Secured Borrowings' },
  { key: 'unsecured_borrowings', label: 'Unsecured Borrowings' },
  { key: 'deferred_tax_liabilities', label: 'Deferred Tax Liabilities' },
  { key: 'other_long_term_liabilities', label: 'Other Long-Term Liabilities' },
  { key: 'long_term_provisions', label: 'Long Term Provisions' },
];

const BS_CURRENT_LIAB_ROWS = [
  { key: 'short_term_borrowings', label: 'Short-Term Borrowings' },
  { key: 'trade_payables', label: 'Trade Payables' },
  { key: 'other_current_liabilities', label: 'Other Current Liabilities' },
  { key: 'short_term_provisions', label: 'Short-Term Provisions' },
];

const BS_FIXED_ASSETS_ROWS = [
  { key: 'tangible_intangible_assets', label: 'Tangible & Intangible Assets' },
  { key: 'gross_block', label: 'Gross Block' },
  { key: 'accumulated_depreciation', label: 'Less: Accumulated Depreciation' },
];

const BS_NONCURRENT_ASSETS_ROWS = [
  { key: 'non_current_investments', label: 'Non-Current Investments' },
  { key: 'deferred_tax_assets', label: 'Deferred Tax Assets' },
  { key: 'long_term_loans_advances', label: 'Long-Term Loans and Advances' },
  { key: 'other_non_current_assets', label: 'Other Non-Current Assets' },
];

const BS_CURRENT_ASSETS_ROWS = [
  { key: 'current_investments', label: 'Current Investments' },
  { key: 'inventories', label: 'Inventories' },
  { key: 'trade_receivables_more_6m', label: 'Trade Receivables - More than 6 Months' },
  { key: 'trade_receivables_less_6m', label: 'Trade Receivables - Less than 6 Months' },
  { key: 'cash_bank_balances', label: 'Cash and Bank Balances' },
  { key: 'short_term_loans_advances', label: 'Short-Term Loans and Advances' },
  { key: 'other_current_assets', label: 'Other Current Assets' },
];

const ALL_ROWS = [
  ...PL_INCOME_ROWS,
  ...PL_COGS_ROWS,
  ...PL_EXPENSE_ROWS,
  ...PL_BELOW_ROWS,
  ...BS_EQUITY_ROWS,
  ...BS_NONCURRENT_LIAB_ROWS,
  ...BS_CURRENT_LIAB_ROWS,
  ...BS_FIXED_ASSETS_ROWS,
  ...BS_NONCURRENT_ASSETS_ROWS,
  ...BS_CURRENT_ASSETS_ROWS,
];

const buildDefaultData = () => {
  const data = {
    cy_label: '2021-22',
    py_label: '2020-21',
    cy_emi_interest_od_cc: '0',
    py_emi_interest_od_cc: '0',
  };
  ALL_ROWS.forEach((r) => {
    data[`cy_${r.key}`] = '';
    data[`py_${r.key}`] = '';
  });
  return data;
};

const n = (v) => parseFloat(v) || 0;
const div = (a, b) => (b ? a / b : 0);
const diffPct = (cy, py) => (py ? (cy - py) / py : 0);

const fmt = (v) =>
  (v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtPct = (v) => `${((v || 0) * 100).toFixed(2)}%`;

// ---- Core computation engine, mirrors the Excel "Financial" sheet ----
const computeYear = (d, p) => {
  const g = (k) => n(d[`${p}_${k}`]);

  const revenue = g('revenue_from_operations');
  const otherIncome = g('other_income');
  const totalRevenue = revenue + otherIncome;

  const openingStock = g('opening_stock');
  const purchases = g('purchases');
  const closingStock = g('closing_stock');
  const directExpenses = g('direct_expenses');
  const cogs = openingStock + purchases - closingStock + directExpenses;
  const grossProfit = totalRevenue - cogs;

  const employeeBenefit = g('employee_benefit_expenses');
  const selling = g('selling_expenses');
  const bankCharges = g('bank_charges');
  const interestPaid = g('interest_paid');
  const depreciation = g('depreciation_amortisation_expenses');
  const partnersSalary = g('partners_director_salary');
  const otherExpenses = g('other_expenses');
  const totalExpenses =
    employeeBenefit + selling + bankCharges + interestPaid + depreciation + partnersSalary + otherExpenses;

  const pbeit = grossProfit - totalExpenses; // Profit Before Exceptional Item and Tax
  const exceptionalItem = g('exceptional_item');
  const pbt = pbeit - exceptionalItem;
  const tax = g('tax');
  const pat = pbt - tax;

  const shareCapital = g('share_capital');
  const reserves = g('reserves_and_surplus');
  const quasiEquity = g('quasi_equity');
  const totalEquity = shareCapital + reserves + quasiEquity;

  const securedBorrowings = g('secured_borrowings');
  const unsecuredBorrowings = g('unsecured_borrowings');
  const deferredTaxLiab = g('deferred_tax_liabilities');
  const otherLongTermLiab = g('other_long_term_liabilities');
  const longTermProvisions = g('long_term_provisions');
  const totalNonCurrentLiab =
    securedBorrowings + unsecuredBorrowings + deferredTaxLiab + otherLongTermLiab + longTermProvisions;

  const shortTermBorrowings = g('short_term_borrowings');
  const tradePayables = g('trade_payables');
  const otherCurrentLiab = g('other_current_liabilities');
  const shortTermProvisions = g('short_term_provisions');
  const totalCurrentLiab = shortTermBorrowings + tradePayables + otherCurrentLiab + shortTermProvisions;

  const totalLiabilities = totalEquity + totalNonCurrentLiab + totalCurrentLiab;

  const tangibleIntangible = g('tangible_intangible_assets');
  const grossBlock = g('gross_block');
  const accDepreciation = g('accumulated_depreciation');
  const netFixedAssets = tangibleIntangible + grossBlock - accDepreciation;

  const nonCurrentInvestments = g('non_current_investments');
  const deferredTaxAssets = g('deferred_tax_assets');
  const longTermLoansAdvances = g('long_term_loans_advances');
  const otherNonCurrentAssets = g('other_non_current_assets');
  const totalNonCurrentAssets =
    nonCurrentInvestments + deferredTaxAssets + longTermLoansAdvances + otherNonCurrentAssets;

  const currentInvestments = g('current_investments');
  const inventories = g('inventories');
  const receivablesMore6m = g('trade_receivables_more_6m');
  const receivablesLess6m = g('trade_receivables_less_6m');
  const cashBank = g('cash_bank_balances');
  const shortTermLoansAdvances = g('short_term_loans_advances');
  const otherCurrentAssets = g('other_current_assets');
  const totalCurrentAssets =
    currentInvestments +
    inventories +
    receivablesMore6m +
    receivablesLess6m +
    cashBank +
    shortTermLoansAdvances +
    otherCurrentAssets;

  const totalAssets = totalCurrentAssets + totalNonCurrentAssets + netFixedAssets;
  const difference = totalAssets - totalLiabilities;

  // ---- Ratios ----
  const grossProfitPct = div(grossProfit, revenue);
  const pbtPct = div(pbt, revenue);
  const patPct = div(pat, revenue);
  const cashProfit = pat + depreciation;
  const cashProfitRatio = div(cashProfit, totalRevenue);
  const tnw = totalEquity;
  const tolTnw = div(totalNonCurrentLiab + totalCurrentLiab, totalEquity);
  const debtEquityRatio = div(securedBorrowings + unsecuredBorrowings + shortTermBorrowings, totalEquity);
  const quickRatio = div(totalCurrentAssets - inventories, totalCurrentLiab);
  const currentRatio = div(totalCurrentAssets, totalCurrentLiab);
  const debtorDays = div(receivablesLess6m, revenue) * 365;
  const creditorDays = div(tradePayables, purchases) * 365;
  const stockDays = div(inventories, cogs) * 365;
  const pctDebtorsGt6m = div(receivablesMore6m, revenue);
  const ebitda = pbeit + depreciation + interestPaid;
  const totalDebtEbitda = div(securedBorrowings + unsecuredBorrowings + shortTermBorrowings, ebitda);
  const roe = div(pat, totalEquity);
  const roce = div(pbt + interestPaid, totalAssets - otherCurrentLiab);
  const ebitdaPct = div(ebitda, totalRevenue);

  // ---- Pre-funding FOIR Computation ----
  const interestDepAmort = depreciation + interestPaid;
  const directorsRemuneration = partnersSalary;
  const ebidtaFoir = pat + interestDepAmort + directorsRemuneration;
  const emiInterest = g('emi_interest_od_cc');
  const foir = div(emiInterest, ebidtaFoir);

  return {
    revenue,
    otherIncome,
    totalRevenue,
    openingStock,
    purchases,
    closingStock,
    directExpenses,
    cogs,
    grossProfit,
    employeeBenefit,
    selling,
    bankCharges,
    interestPaid,
    depreciation,
    partnersSalary,
    otherExpenses,
    totalExpenses,
    pbeit,
    exceptionalItem,
    pbt,
    tax,
    pat,
    shareCapital,
    reserves,
    quasiEquity,
    totalEquity,
    securedBorrowings,
    unsecuredBorrowings,
    deferredTaxLiab,
    otherLongTermLiab,
    longTermProvisions,
    totalNonCurrentLiab,
    shortTermBorrowings,
    tradePayables,
    otherCurrentLiab,
    shortTermProvisions,
    totalCurrentLiab,
    totalLiabilities,
    tangibleIntangible,
    grossBlock,
    accDepreciation,
    netFixedAssets,
    nonCurrentInvestments,
    deferredTaxAssets,
    longTermLoansAdvances,
    otherNonCurrentAssets,
    totalNonCurrentAssets,
    currentInvestments,
    inventories,
    receivablesMore6m,
    receivablesLess6m,
    cashBank,
    shortTermLoansAdvances,
    otherCurrentAssets,
    totalCurrentAssets,
    totalAssets,
    difference,
    ratios: {
      grossProfitPct,
      pbtPct,
      patPct,
      cashProfit,
      cashProfitRatio,
      tnw,
      tolTnw,
      debtEquityRatio,
      quickRatio,
      currentRatio,
      debtorDays,
      creditorDays,
      stockDays,
      pctDebtorsGt6m,
      totalDebtEbitda,
      roe,
      roce,
      ebitda,
      ebitdaPct,
    },
    foir: {
      pat,
      interestDepAmort,
      directorsRemuneration,
      ebidtaFoir,
      emiInterest,
      foir,
    },
  };
};

// ----------------------------------------------------------------------
const FinancialSheet = (props) => {
  const { leadId } = props;

  const [financialData, setFinancialData] = useState(buildDefaultData());
  const [loadingList, setLoadingList] = useState(false);
  const [preloaderStatus, setPreloaderStatus] = useState(false);

  const getFinancialSheet = async () => {
    if (!leadId) return;
    try {
      setLoadingList(true);

      const obj = { lead_id: leadId };

      const response = await fetch(`${config.API_BASE_URL}getFinancialSheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(obj),
      });

      const result = await response.json();

      if (result?.success === 1) {
        const data = result?.data || {};
        if (data && Object.keys(data).length > 0) {
          setFinancialData({ ...buildDefaultData(), ...data });
        } else {
          setFinancialData(buildDefaultData());
        }
      } else {
        setFinancialData(buildDefaultData());
      }
    } catch (error) {
      console.log('Financial Sheet fetch error:', error);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    getFinancialSheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFinancialData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setPreloaderStatus(true);

      const params = { lead_id: leadId, financialData };

      const response = await fetch(`${config.API_BASE_URL}manageFinancialSheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(params),
      });

      const result = await response.json();

      if (result?.success === 1) {
        toast.success(result.message, { position: 'top-right' });
        getFinancialSheet();
      } else {
        toast.error(result?.message || 'Something went wrong.', { position: 'top-right' });
      }
    } catch (error) {
      console.error('Error in Financial Sheet save:', error);
      toast.error('An error occurred. Please try again.', { position: 'top-right' });
    } finally {
      setPreloaderStatus(false);
    }
  };

  const cyData = computeYear(financialData, 'cy');
  const pyData = computeYear(financialData, 'py');

  // ---- Row renderer for editable P&L / Balance Sheet line items ----
  const renderRow = (row) => {
    const cyVal = n(financialData[`cy_${row.key}`]);
    const pyVal = n(financialData[`py_${row.key}`]);
    return (
      <tr key={row.key}>
        <td>{row.label}</td>
        <td>
          <input
            type="number"
            name={`cy_${row.key}`}
            className="form-control form-control-sm"
            value={financialData[`cy_${row.key}`]}
            onChange={handleChange}
          />
        </td>
        <td>
          <input
            type="number"
            name={`py_${row.key}`}
            className="form-control form-control-sm"
            value={financialData[`py_${row.key}`]}
            onChange={handleChange}
          />
        </td>
        <td className="text-end">{fmtPct(diffPct(cyVal, pyVal))}</td>
      </tr>
    );
  };

  const renderTotalRow = (label, cyVal, pyVal, extraClass = 'table-light fw-bold') => (
    <tr className={extraClass}>
      <td>{label}</td>
      <td>{fmt(cyVal)}</td>
      <td>{fmt(pyVal)}</td>
      <td className="text-end">{fmtPct(diffPct(cyVal, pyVal))}</td>
    </tr>
  );

  const renderHeaderRow = () => (
    <tr className="table-secondary">
      <th style={{ minWidth: 260 }}> </th>
      <th style={{ minWidth: 130 }}>
        <input
          type="text"
          name="cy_label"
          className="form-control form-control-sm fw-bold"
          value={financialData.cy_label}
          onChange={handleChange}
        />
      </th>
      <th style={{ minWidth: 130 }}>
        <input
          type="text"
          name="py_label"
          className="form-control form-control-sm fw-bold"
          value={financialData.py_label}
          onChange={handleChange}
        />
      </th>
      <th style={{ minWidth: 100 }} className="text-end">
        Difference
      </th>
    </tr>
  );

  const ratioRow = (label, cyVal, pyVal, isPct = false) => (
    <tr key={label}>
      <td>{label}</td>
      <td>{isPct ? fmtPct(cyVal) : fmt(cyVal)}</td>
      <td>{isPct ? fmtPct(pyVal) : fmt(pyVal)}</td>
    </tr>
  );

  return (
    <div className="financial_sheet_section mt-3">
      {loadingList ? (
        <p>Loading Financial sheet...</p>
      ) : (
        <div>
          <div className="row">
            {/* ---------------- LEFT: Profit & Loss + Balance Sheet ---------------- */}
            <div className="col-md-7">
              <h5 className="mb-2">Profit &amp; Loss</h5>

              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead>{renderHeaderRow()}</thead>
                  <tbody>
                    <tr className="table-light">
                      <td colSpan="4">
                        <b>Income</b>
                      </td>
                    </tr>
                    {PL_INCOME_ROWS.map(renderRow)}
                    {renderTotalRow('Total Revenue', cyData.totalRevenue, pyData.totalRevenue)}

                    <tr className="table-light">
                      <td colSpan="4">
                        <b>Expenses</b>
                      </td>
                    </tr>
                    {PL_COGS_ROWS.map(renderRow)}
                    {renderTotalRow('Cost of Goods Sold', cyData.cogs, pyData.cogs)}
                    {renderTotalRow('Gross Profit', cyData.grossProfit, pyData.grossProfit, 'table-warning fw-bold')}

                    {PL_EXPENSE_ROWS.map(renderRow)}
                    {renderTotalRow('Total Expenses', cyData.totalExpenses, pyData.totalExpenses)}

                    {renderTotalRow(
                      'Profit Before Exceptional Item and Tax',
                      cyData.pbeit,
                      pyData.pbeit
                    )}
                    {PL_BELOW_ROWS.map(renderRow)}
                    {renderTotalRow('Profit Before Tax (PBT)', cyData.pbt, pyData.pbt)}
                    {renderTotalRow(
                      'Profit After Tax (PAT)',
                      cyData.pat,
                      pyData.pat,
                      'table-success fw-bold'
                    )}
                  </tbody>
                </table>
              </div>

              <h5 className="mt-4 mb-2">Balance Sheet</h5>
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead>{renderHeaderRow()}</thead>
                  <tbody>
                    <tr className="table-light">
                      <td colSpan="4">
                        <b>Equity &amp; Liabilities</b>
                      </td>
                    </tr>
                    <tr className="table-light">
                      <td colSpan="4">Shareholder's Fund</td>
                    </tr>
                    {BS_EQUITY_ROWS.map(renderRow)}
                    {renderTotalRow('Total Equity', cyData.totalEquity, pyData.totalEquity)}

                    <tr className="table-light">
                      <td colSpan="4">Non Current Liabilities</td>
                    </tr>
                    {BS_NONCURRENT_LIAB_ROWS.map(renderRow)}
                    {renderTotalRow(
                      'Total Non Current Liabilities',
                      cyData.totalNonCurrentLiab,
                      pyData.totalNonCurrentLiab
                    )}

                    <tr className="table-light">
                      <td colSpan="4">Current Liabilities</td>
                    </tr>
                    {BS_CURRENT_LIAB_ROWS.map(renderRow)}
                    {renderTotalRow(
                      'Total Current Liabilities',
                      cyData.totalCurrentLiab,
                      pyData.totalCurrentLiab
                    )}

                    {renderTotalRow(
                      'Total Liabilities',
                      cyData.totalLiabilities,
                      pyData.totalLiabilities,
                      'table-primary fw-bold'
                    )}

                    <tr className="table-light">
                      <td colSpan="4">
                        <b>Assets</b>
                      </td>
                    </tr>
                    <tr className="table-light">
                      <td colSpan="4">Fixed Assets</td>
                    </tr>
                    {BS_FIXED_ASSETS_ROWS.map(renderRow)}
                    {renderTotalRow('Net Fixed Assets', cyData.netFixedAssets, pyData.netFixedAssets)}

                    <tr className="table-light">
                      <td colSpan="4">Non-Current Assets</td>
                    </tr>
                    {BS_NONCURRENT_ASSETS_ROWS.map(renderRow)}
                    {renderTotalRow(
                      'Total Non-Current Assets',
                      cyData.totalNonCurrentAssets,
                      pyData.totalNonCurrentAssets
                    )}

                    <tr className="table-light">
                      <td colSpan="4">Current Assets</td>
                    </tr>
                    {BS_CURRENT_ASSETS_ROWS.map(renderRow)}
                    {renderTotalRow(
                      'Total Current Assets',
                      cyData.totalCurrentAssets,
                      pyData.totalCurrentAssets
                    )}

                    {renderTotalRow(
                      'Total Assets',
                      cyData.totalAssets,
                      pyData.totalAssets,
                      'table-primary fw-bold'
                    )}
                    {renderTotalRow('Difference', cyData.difference, pyData.difference, 'table-danger')}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ---------------- RIGHT: Ratios + FOIR ---------------- */}
            <div className="col-md-5">
              <h5 className="mb-2">Ratios</h5>
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead>
                    <tr className="table-secondary">
                      <th>Ratio</th>
                      <th>{financialData.cy_label}</th>
                      <th>{financialData.py_label}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ratioRow('Gross Profit (%)', cyData.ratios.grossProfitPct, pyData.ratios.grossProfitPct, true)}
                    {ratioRow('PBT (%)', cyData.ratios.pbtPct, pyData.ratios.pbtPct, true)}
                    {ratioRow('PAT (%)', cyData.ratios.patPct, pyData.ratios.patPct, true)}
                    {ratioRow('Cash Profit', cyData.ratios.cashProfit, pyData.ratios.cashProfit)}
                    {ratioRow(
                      'Cash Profit Ratio',
                      cyData.ratios.cashProfitRatio,
                      pyData.ratios.cashProfitRatio,
                      true
                    )}
                    {ratioRow('TNW', cyData.ratios.tnw, pyData.ratios.tnw)}
                    {ratioRow('TOL/TNW', cyData.ratios.tolTnw, pyData.ratios.tolTnw)}
                    {ratioRow('Debt Equity Ratio', cyData.ratios.debtEquityRatio, pyData.ratios.debtEquityRatio)}
                    {ratioRow('Quick Ratio', cyData.ratios.quickRatio, pyData.ratios.quickRatio)}
                    {ratioRow('Current Ratio', cyData.ratios.currentRatio, pyData.ratios.currentRatio)}
                    {ratioRow(
                      'Debtor Days (excluding > 6 months debtors)',
                      cyData.ratios.debtorDays,
                      pyData.ratios.debtorDays
                    )}
                    {ratioRow('Creditor Days', cyData.ratios.creditorDays, pyData.ratios.creditorDays)}
                    {ratioRow('Stock Days', cyData.ratios.stockDays, pyData.ratios.stockDays)}
                    {ratioRow(
                      '% Debtors > 6 Months',
                      cyData.ratios.pctDebtorsGt6m,
                      pyData.ratios.pctDebtorsGt6m,
                      true
                    )}
                    {ratioRow(
                      'Total Debt/EBITDA',
                      cyData.ratios.totalDebtEbitda,
                      pyData.ratios.totalDebtEbitda
                    )}
                    {ratioRow('ROE', cyData.ratios.roe, pyData.ratios.roe)}
                    {ratioRow('ROCE', cyData.ratios.roce, pyData.ratios.roce)}
                    {ratioRow('EBITDA', cyData.ratios.ebitda, pyData.ratios.ebitda)}
                    {ratioRow('EBITDA %', cyData.ratios.ebitdaPct, pyData.ratios.ebitdaPct, true)}
                  </tbody>
                </table>
              </div>

              <h5 className="mt-4 mb-2">Pre-funding FOIR Computation</h5>
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead>
                    <tr className="table-secondary">
                      <th>Particulars</th>
                      <th>Ref.</th>
                      <th>{financialData.cy_label}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>PAT</td>
                      <td>A</td>
                      <td>{fmt(cyData.foir.pat)}</td>
                    </tr>
                    <tr>
                      <td>Interest, Depreciation and Amortization</td>
                      <td>B</td>
                      <td>{fmt(cyData.foir.interestDepAmort)}</td>
                    </tr>
                    <tr>
                      <td>Director's Remuneration/Partner's Salary</td>
                      <td>C</td>
                      <td>{fmt(cyData.foir.directorsRemuneration)}</td>
                    </tr>
                    <tr className="table-light fw-bold">
                      <td>EBIDTA</td>
                      <td>D = (A+B+C)</td>
                      <td>{fmt(cyData.foir.ebidtaFoir)}</td>
                    </tr>
                    <tr>
                      <td>
                        EMI + Interest (OD/CC)
                        <br />
                        <small className="text-muted">
                          (Sum of All loan EMI*12) + Interest paid on CC/OD a/c for the year
                        </small>
                      </td>
                      <td>E</td>
                      <td>
                        <input
                          type="number"
                          name="cy_emi_interest_od_cc"
                          className="form-control form-control-sm"
                          value={financialData.cy_emi_interest_od_cc}
                          onChange={handleChange}
                        />
                      </td>
                    </tr>
                    <tr className="table-warning fw-bold">
                      <td>FOIR</td>
                      <td>F = E/D</td>
                      <td>{fmtPct(cyData.foir.foir)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="row mx-0 mt-3">
            <div className="col-md-12">
              <div className="form-group mb-0">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={preloaderStatus}>
                  {preloaderStatus ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialSheet;